/**
 * PasswordRequirements Component
 * Reusable component for displaying password strength requirements and validation status
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.requirements - Password requirements object from usePasswordValidation hook
 * @param {Object} props.strength - Password strength object from usePasswordValidation hook
 * @param {Function} props.getRequirementDescriptions - Function to get requirement descriptions
 * @param {boolean} props.showStrength - Whether to show password strength indicator
 * @param {boolean} props.compact - Whether to use compact display mode
 * @param {string} props.variant - Display variant ('default', 'outlined', 'minimal')
 */

import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  Paper,
  LinearProgress,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

// Styled components following Material-UI patterns
const RequirementsContainer = styled(Paper)(({ theme, variant }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  backgroundColor: variant === 'minimal' ? 'transparent' : theme.palette.background.default,
  border: variant === 'outlined' ? `1px solid ${theme.palette.divider}` : 'none',
  boxShadow: variant === 'minimal' ? 'none' : undefined,
}));

const RequirementItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.5, 0),
  minHeight: 'auto',
}));

const RequirementText = styled(Typography)(({ theme, isvalid }) => ({
  color: isvalid 
    ? theme.palette.success.main 
    : theme.palette.text.secondary,
  fontWeight: isvalid ? 500 : 400,
  fontSize: '0.875rem',
}));

const StrengthIndicator = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const StrengthProgress = styled(LinearProgress)(({ theme, strengthlevel }) => {
  const getColor = () => {
    switch (strengthlevel) {
      case 'weak':
        return theme.palette.error.main;
      case 'fair':
        return theme.palette.warning.main;
      case 'good':
        return theme.palette.info.main;
      case 'strong':
        return theme.palette.success.main;
      default:
        return theme.palette.error.main;
    }
  };

  return {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.grey[200],
    '& .MuiLinearProgress-bar': {
      backgroundColor: getColor(),
      borderRadius: 4,
    },
  };
});

/**
 * PasswordRequirements Component
 */
const PasswordRequirements = ({
  requirements = {},
  strength = { score: 0, level: 'weak', feedback: '' },
  getRequirementDescriptions,
  showStrength = true,
  compact = false,
  variant = 'default',
  ...props
}) => {
  // Get requirement descriptions
  const descriptions = getRequirementDescriptions ? getRequirementDescriptions() : {
    minLength: 'At least 8 characters',
    hasNumber: 'Contains a number',
    hasUpper: 'Contains an uppercase letter',
    hasLower: 'Contains a lowercase letter',
    hasSpecial: 'Contains a special character',
    passwordsMatch: 'Passwords match',
  };

  /**
   * Renders a single requirement item
   */
  const renderRequirement = (key, text, isMet) => (
    <RequirementItem key={key} disablePadding>
      <ListItemIcon sx={{ minWidth: 32 }}>
        {isMet ? (
          <CheckCircleIcon 
            color="success" 
            sx={{ fontSize: compact ? '1rem' : '1.25rem' }}
            aria-label="Requirement met"
          />
        ) : (
          <CancelIcon 
            color="disabled" 
            sx={{ fontSize: compact ? '1rem' : '1.25rem' }}
            aria-label="Requirement not met"
          />
        )}
      </ListItemIcon>
      <RequirementText 
        variant={compact ? "caption" : "body2"} 
        isvalid={isMet}
        component="span"
      >
        {text}
      </RequirementText>
    </RequirementItem>
  );

  /**
   * Gets strength level color for chips
   */
  const getStrengthChipColor = () => {
    switch (strength.level) {
      case 'weak':
        return 'error';
      case 'fair':
        return 'warning';
      case 'good':
        return 'info';
      case 'strong':
        return 'success';
      default:
        return 'default';
    }
  };

  // Don't render if no requirements
  if (!requirements || Object.keys(requirements).length === 0) {
    return null;
  }

  return (
    <RequirementsContainer 
      elevation={variant === 'minimal' ? 0 : 1} 
      variant={variant}
      {...props}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <SecurityIcon 
          sx={{ 
            mr: 1, 
            fontSize: compact ? '1.1rem' : '1.25rem',
            color: 'text.secondary' 
          }} 
        />
        <Typography 
          variant={compact ? "subtitle2" : "subtitle1"} 
          component="h3"
          sx={{ fontWeight: 600 }}
        >
          Password Requirements
        </Typography>
      </Box>

      {/* Password Strength Indicator */}
      {showStrength && strength.score > 0 && (
        <StrengthIndicator>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Password Strength:
            </Typography>
            <Chip 
              label={strength.level.charAt(0).toUpperCase() + strength.level.slice(1)}
              color={getStrengthChipColor()}
              size="small"
              variant="outlined"
            />
          </Box>
          <StrengthProgress 
            variant="determinate" 
            value={strength.score} 
            strengthlevel={strength.level}
            aria-label={`Password strength: ${strength.level} (${strength.score}%)`}
          />
          {strength.feedback && (
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ mt: 0.5, display: 'block' }}
            >
              {strength.feedback}
            </Typography>
          )}
        </StrengthIndicator>
      )}

      {/* Requirements List */}
      <List 
        dense={compact} 
        sx={{ p: 0 }}
        role="list"
        aria-label="Password requirements"
      >
        {Object.entries(requirements).map(([key, isMet]) => {
          const description = descriptions[key];
          if (!description) return null;
          
          return renderRequirement(key, description, isMet);
        })}
      </List>

      {/* Accessibility summary */}
      <Box sx={{ sr: 'only' }}>
        <Typography variant="caption">
          {Object.values(requirements).filter(Boolean).length} of {Object.keys(requirements).length} requirements met
        </Typography>
      </Box>
    </RequirementsContainer>
  );
};

export default PasswordRequirements; 